import { useRef } from 'react';

interface LandingHeroProps {
  onStartSetup: () => void;
}

export function LandingHero({ onStartSetup }: LandingHeroProps) {
  return (
    <section className="hero">
      <h2 className="hero-title">Configure your Cronyl Card</h2>
      <p className="hero-subtitle">
        Configure your Cronyl NFC Card in less than one minute.
        <br />
        Securely connect your wallet to your Cronyl Card without installing an app.
      </p>
      <button type="button" className="btn btn-hero" onClick={onStartSetup}>
        Start Setup
      </button>
    </section>
  );
}

const STEPS = [
  {
    num: 1,
    title: 'Scan your Cronyl Card',
    text: 'Tap your card with your phone. Your browser opens automatically — no app required.',
  },
  {
    num: 2,
    title: 'Enter your wallet details',
    text: 'Choose your wallet, blockchain, cryptocurrency and enter your wallet address.',
  },
  {
    num: 3,
    title: 'Save your configuration',
    text: 'Your payment settings are securely linked to your card ID.',
  },
  {
    num: 4,
    title: 'Card locked permanently',
    text: 'After saving, your card configuration is locked and cannot be changed.',
  },
];

export function HowItWorks() {
  return (
    <section className="how-it-works">
      <h3 className="section-title">How it works</h3>
      <div className="steps-grid">
        {STEPS.map((step) => (
          <article key={step.num} className="step-card">
            <span className="step-card-num">{step.num}</span>
            <h4 className="step-card-title">{step.title}</h4>
            <p className="step-card-text">{step.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ImportantNotice() {
  return (
    <section className="important-box">
      <h3 className="important-title">Important</h3>
      <p>Your Cronyl Card can only be configured once.</p>
      <p>Please verify all wallet information carefully before continuing.</p>
      <p>Once the setup has been completed, the stored wallet address can no longer be changed.</p>
    </section>
  );
}

export function useScrollToSetup() {
  const setupRef = useRef<HTMLElement>(null);

  const scrollToSetup = () => {
    setupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return { setupRef, scrollToSetup };
}
